
import { useState, useEffect } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import Loader from "../../components/Loader/Loader";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import crossBtn from "../../assets/images/clear.png";
import { Link, useNavigate, useParams } from "react-router-dom";
import logoLg from "../../assets/images/lg-logo.png";
import { withRouter, notification } from "../../utils/utility";
import { sendPostRequest } from "../../actions/app-actions";
import {
  getUserDashboardLink,
  getUserRoles,
  getUserBusinesses,
  shouldShowPortalSelectionAfterLogin,
  shouldSkipFeatureFetchOnLogin,
} from "../../utils/authHelper";
import CryptoJS from "crypto-js";
import { USER_TYPE_PREFIX } from "../../utils/prefixes";
import { USER_TYPES } from "../../routes/types/private";
import isEmpty from "../../validation/is-empty";
import setAuthToken from "../../utils/setAuthToken";
import { setCurrentUser } from "../../actions/authActions";
import { fetchUserFeatures } from "../../actions/featureAccessActions";

const formatLockDuration = (seconds) => {
  const secs = Math.max(1, Math.ceil(Number(seconds) || 0));
  if (secs >= 60) {
    const mins = Math.ceil(secs / 60);
    return mins === 1 ? "1 minute" : `${mins} minutes`;
  }
  return secs === 1 ? "1 second" : `${secs} seconds`;
};

const buildLockMessage = (seconds) =>
  `Too many failed attempts. You are temporarily blocked. Please try again in ${formatLockDuration(seconds)}.`;

const Google2FA = (props) => {

    const { challenge_token } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [lockSecondsRemaining, setLockSecondsRemaining] = useState(0);
    const featuresLoaded = useSelector((state) => state.featureAccess.loaded);

    const Schema = Yup.object().shape({
        otp: Yup.string()
            .matches(/^\d+$/, 'Security Code must contain digits only')
            .min(6, "Security Code too short!")
            .max(6, "Maximum six digit are allowed")
            .required("Security Code is required.")
    });

    useEffect(() => {
        if (!challenge_token) {
            navigate("/admin/login");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [challenge_token]);

    useEffect(() => {
        if (lockSecondsRemaining <= 0) {
            return undefined;
        }

        const timer = setInterval(() => {
            setLockSecondsRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
        }, 1000);

        return () => clearInterval(timer);
    }, [lockSecondsRemaining > 0]);

    useEffect(() => {
        if (!props.auth.isAuthenticated) {
          return;
        }

        const userType = localStorage.userType;
        if (userType === USER_TYPES.admin && !featuresLoaded) {
          return;
        }

        setIsLoading(false);
        // based on usertype redirect to user specific dashboard
        if (userType) {
          if (shouldShowPortalSelectionAfterLogin()) {
            localStorage.removeItem("userType");
            localStorage.removeItem("businessId");
            navigate("/admin/user-type");
          } else {
            navigate(getUserDashboardLink(userType));
          }
        } else {
          const userRoles = getUserRoles();
          const userBusinesses = getUserBusinesses();
    
          // Getting business ids
          let businessIds = [];
          if (!isEmpty(userBusinesses)) {
            for (let userBusiness in userBusinesses) {
              businessIds.push(userBusinesses[userBusiness]);
            }
          }
    
          if (
            userRoles.length === 1 &&
            (businessIds.length === 0 || businessIds.length === 1) &&
            !shouldShowPortalSelectionAfterLogin()
          ) {
            localStorage.setItem(
              "userType",
              CryptoJS.SHA256(USER_TYPE_PREFIX + userRoles.pop())
            );
            // for admin user save business id in local storage
            if (
              localStorage.userType === USER_TYPES.admin &&
              businessIds.length === 1
            ) {
              localStorage.setItem("businessId", businessIds.pop());
            }

            if (localStorage.userType === USER_TYPES.admin && !featuresLoaded) {
              return;
            }

            navigate(getUserDashboardLink(localStorage.getItem("userType")));
          } else {
            navigate("/admin/user-type");
          }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.auth.isAuthenticated, featuresLoaded]);

    const applyLockout = (seconds, formikHelpers) => {
        const retryAfter = Math.max(1, Math.ceil(Number(seconds) || 60));
        const message = buildLockMessage(retryAfter);
        setLockSecondsRemaining(retryAfter);
        notification.error(message);
        // Banner + disabled controls show the lock; clear field error so it
        // does not linger after the countdown ends.
        formikHelpers.setFieldError("otp", undefined);
    };

    const handleVerifyError = (error, formikHelpers) => {
        const response = error?.response;
        const data = response?.data || {};
        const status = response?.status;
        const payload = data?.data && typeof data.data === "object" ? data.data : {};
        const errorCode = payload.error || data.error || null;
        const apiMessage = typeof data.message === "string" ? data.message : null;
        const retryAfterHeader = response?.headers?.["retry-after"];
        const retryAfter =
            payload.retry_after ??
            data?.retry_after ??
            (retryAfterHeader ? Number(retryAfterHeader) : null);

        if (
            errorCode === "2fa_exhausted" ||
            errorCode === "2fa_challenge_invalid"
        ) {
            const message =
                apiMessage ||
                (errorCode === "2fa_exhausted"
                    ? "Too many failed attempts. Please log in again."
                    : "Your verification session has expired or is invalid. Please log in again.");
            notification.error(message);
            formikHelpers.setFieldError("otp", message);
            setTimeout(() => navigate("/admin/login"), 1500);
            return;
        }

        if (errorCode === "2fa_locked" || status === 429) {
            applyLockout(retryAfter || 60, formikHelpers);
            return;
        }

        if (apiMessage && !Array.isArray(data.message)) {
            notification.error(apiMessage);
            formikHelpers.setFieldError("otp", apiMessage);
            return;
        }

        notification.error("Invalid OTP, please try again");
        formikHelpers.setFieldError("otp", "Invalid OTP, please try again");
    };
    
    const formikProps = {
        initialValues: {
            otp: "",
        },
        validationSchema: Schema,
        onSubmit: (values, formikHelpers) => {
            if (lockSecondsRemaining > 0) {
                applyLockout(lockSecondsRemaining, formikHelpers);
                return;
            }

            setIsLoading(true);
            props.sendPostRequest({
                url: `/verify-2fa`,
                data: {
                    otp: values.otp,
                    challenge_token: decodeURIComponent(challenge_token)
                },
                onSuccess: function (response) {
                    if (response.success) {
                        const { access_token, jwt_custom_claims } = response.data;
                        // Successful login replaces any impersonation session.
                        localStorage.removeItem("impersonator_session");
                        localStorage.removeItem("userType");
                        localStorage.removeItem("businessId");
                        localStorage.setItem("config", JSON.stringify(jwt_custom_claims));
                        localStorage.setItem("userTimezone", jwt_custom_claims.timezone);
                        localStorage.setItem("userTimezoneId", jwt_custom_claims.timezone_id);
                        localStorage.setItem("page", jwt_custom_claims.page);
                        // Set token to ls
                        localStorage.setItem("jwtToken", access_token);
                        // Set token to Auth header
                        setAuthToken(access_token);
                        dispatch(setCurrentUser(jwt_custom_claims.user));
                        if (!shouldSkipFeatureFetchOnLogin()) {
                          dispatch(fetchUserFeatures());
                        }
                    }
                    setIsLoading(false);
                },
                onError: function (error) {
                    handleVerifyError(error, formikHelpers);
                    setIsLoading(false);
                },
            });
        },
    };

    const isLocked = lockSecondsRemaining > 0;
    const lockBannerMessage = isLocked
        ? buildLockMessage(lockSecondsRemaining)
        : null;

    return (
        <>
            <Loader isShowLoader={isLoading} />
            <Formik {...formikProps}>
                {(formik) => {
                    const { values, handleChange, handleSubmit, handleBlur } = formik;
                    return (
                        <div className="signup-process">
                            <div className="logo-side">
                                <img src={logoLg} alt="" />
                            </div>
                            <div className="signup">
                                <div className="cross-btn">
                                    <Link to="/">
                                        <img width="33" src={crossBtn} alt="" />
                                    </Link>
                                </div>
                                <div className="signup-form">
                                    <form onSubmit={handleSubmit}>
                                        <h1 className="mb-2">Enter Security Code</h1>
                                        <p className="r-p">Open your authentication app and enter the security code it provides.</p>
                                        {lockBannerMessage && (
                                            <p className="error mb-3" role="alert">
                                                {lockBannerMessage}
                                            </p>
                                        )}
                                        <div className="form-group mb-0">
                                        <label className="same-label" htmlFor="email">
                                            Security Code
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control same-input"
                                            name="otp"
                                            id="otp"
                                            placeholder="Enter Security Code"
                                            value={values.otp}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={isLocked}
                                        />
                                        </div>
                                        <ErrorMessage
                                            name="otp"
                                            render={(error) => <span className="error">{error}</span>}
                                        />
                                        <button
                                            type="submit"
                                            className="btn btn-app"
                                            disabled={isLocked}
                                        >
                                            {isLocked
                                                ? `Try again in ${formatLockDuration(lockSecondsRemaining)}`
                                                : "Verify"}
                                        </button>
                                        <p className="login-here">
                                            Don't have a account? <Link to="/signup">SignUp Now</Link>
                                        </p>
                                    </form>
                                </div>
                                <div className="signup-footer">
                                    <ul className="languages d-none">
                                        <li>English (UK)</li>
                                        <li>हिन्दी</li>
                                        <li>ਪੰਜਾਬੀ</li>
                                        <li>বাংলা</li>
                                        <li>Deutsch</li>
                                        <li>ગુજરાતી</li>
                                        <li>Español</li>
                                    </ul>
                                    <p>Copyright © 2025 MeetOcto.</p>
                                </div>
                            </div>
                        </div>
                    );
                }}
            </Formik>
        </>
    )

}


const mapStateToProps = (state) => ({
  auth: state.auth,
  errors: state.errors,
});

export default connect(mapStateToProps, {sendPostRequest} )(withRouter(Google2FA));
